import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8001';

// Extract target section from feedback text
const extractTargetSection = (feedback) => {
  const feedbackLower = feedback.toLowerCase();
  
  if (feedbackLower.includes('summary') || feedbackLower.includes('story')) {
    return 'summary';
  } else if (feedbackLower.includes('learning outcome') || feedbackLower.includes('outcome')) {
    return 'learning_outcomes';
  } else if (feedbackLower.includes('keyword') || feedbackLower.includes('term')) {
    return 'keywords';
  } else if (feedbackLower.includes('concept')) {
    return 'concepts';
  }
  
  return 'summary'; // Default to summary if unclear
};

const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 300000, // Increased to 2 minutes for document processing
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor
api.interceptors.request.use(
  (config) => {
    return config;
  },
  (error) => {
    console.error('❌ API Request Error:', error);
    return Promise.reject(error);
  }
);

// Response interceptor
api.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    console.error('❌ API Response Error:', error.response?.data || error.message);
    return Promise.reject(error);
  }
);

// Workflow API functions
export const workflowAPI = {
  // Start a new workflow
  startWorkflow: async (data) => {
    try {
      const response = await api.post('/workflow/start', data);
      return response.data;
    } catch (error) {
      throw new Error(`Failed to start workflow: ${error.response?.data?.detail || error.message}`);
    }
  },

  // Approve a stage
  approveStage: async (sessionId, approved, changesRequested = null) => {
    try {
      const response = await api.post('/workflow/approve', {
        session_id: sessionId,
        approved,
        changes_requested: changesRequested
      });
      return response.data;
    } catch (error) {
      throw new Error(`Failed to approve stage: ${error.response?.data?.detail || error.message}`);
    }
  },

  // Process changes for a stage
  processChanges: async (sessionId, stage, changesRequested, existingContent = null) => {
    const startTime = Date.now();
    
    try {
      
      const requestPayload = {
        session_id: sessionId,
        stage,
        changes_requested: changesRequested
      };
      
      // Add existing content if provided
      if (existingContent) {
        requestPayload.existing_content = existingContent;
      }
      
      
      
      // Use appropriate payload based on stage
      let finalPayload;
      if (stage === 'story') {
        // For story, use enhanced payload with section information
        finalPayload = {
          session_id: sessionId,
          stage,
          changes_requested: changesRequested,
          target_section: extractTargetSection(changesRequested),
          request_type: 'section_revision'
        };
      } else {
        // For dialogues and questions, use simple payload
        finalPayload = {
          session_id: sessionId,
          stage,
          changes_requested: changesRequested
        };
      }
      
      
      const response = await api.post('/workflow/change', finalPayload);
      
      const endTime = Date.now();
      const duration = endTime - startTime;
      
      
      return response.data;
    } catch (error) {
      const endTime = Date.now();
      const duration = endTime - startTime;
      
      console.error('API: Error in processChanges after', duration, 'ms');
      console.error('API: Error type:', error.name);
      console.error('API: Error message:', error.message);
      console.error('API: Error code:', error.code);
      console.error('API: Error response:', error.response);
      console.error('API: Error config:', error.config);
      
      if (error.code === 'ECONNABORTED') {
        throw new Error('Request timeout - server took too long to respond');
      } else if (error.response) {
        throw new Error(`Server error: ${error.response.status} - ${error.response.data?.detail || error.message}`);
      } else {
        throw new Error(`Network error: ${error.message}`);
      }
    }
  },

  // Request changes for a specific section only
  processSectionChanges: async (sessionId, stage, section, changesRequested, existingContent = null) => {
    try {
      
      const payload = {
        session_id: sessionId,
        stage,
        target_section: section,
        changes_requested: changesRequested,
        request_type: 'section_revision_only'
      };
      
      // Add existing content if provided
      if (existingContent) {
        payload.existing_content = existingContent;
      }
      
      
      const response = await api.post('/workflow/change', payload);
      
      
      return response.data;
    } catch (error) {
      console.error('API: Error in processSectionChanges:', error);
      throw new Error(`Failed to process section changes: ${error.response?.data?.detail || error.message}`);
    }
  },

  // Get current stage content
  getCurrentStage: async (sessionId) => {
    try {
      
      const response = await api.get(`/workflow/${sessionId}/stage`);
      
      
      return response.data;
    } catch (error) {
      console.error('API: Error in getCurrentStage:', error);
      console.error('API: Error response:', error.response);
      console.error('API: Error message:', error.message);
      throw new Error(`Failed to get current stage: ${error.response?.data?.detail || error.message}`);
    }
  },

  // Get session status
  getSessionStatus: async (sessionId) => {
    try {
      const response = await api.get(`/workflow/${sessionId}/status`);
      return response.data;
    } catch (error) {
      throw new Error(`Failed to get session status: ${error.response?.data?.detail || error.message}`);
    }
  },

  // Download formatted content
  downloadContent: async (sessionId) => {
    try {
      const response = await api.get(`/workflow/${sessionId}/download`, {
        responseType: 'blob'
      });
      return response.data;
    } catch (error) {
      throw new Error(`Failed to download content: ${error.response?.data?.detail || error.message}`);
    }
  },

  // List all sessions
  listSessions: async () => {
    try {
      const response = await api.get('/workflow/sessions');
      return response.data;
    } catch (error) {
      throw new Error(`Failed to list sessions: ${error.response?.data?.detail || error.message}`);
    }
  },

  // Health check
  healthCheck: async () => {
    try {
      const response = await api.get('/health');
      return response.data;
    } catch (error) {
      throw new Error(`Health check failed: ${error.response?.data?.detail || error.message}`);
    }
  }
};

export default api;


